// Utility to build API payloads from schema + formData
import dayjs from 'dayjs';

const isObject = (v) => v && typeof v === 'object' && !Array.isArray(v);

const getByPath = (obj, path) => {
  if (!obj || typeof path !== 'string') return undefined;
  return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
};

const normalizeDate = (val) => {
  if (!val) return '';
  // Accept Dayjs, Date, or ISO/string
  if (dayjs.isDayjs?.(val)) return val.format('YYYY-MM-DD');
  const d = dayjs(val);
  return d.isValid() ? d.format('YYYY-MM-DD') : String(val);
};

export function flattenFields(blocks = []) {
  const fields = [];
  blocks.forEach((block) => {
    // If the block itself is a field (has a name), include it
    if (block?.name) {
      fields.push(block);
    }
    // Also include nested fields
    if (Array.isArray(block?.fields)) {
      block.fields.forEach((f) => fields.push(f));
    }
  });
  return fields;
}

export function buildPayloadFromSchema(schema, formData) {
  const isArraySchema = Array.isArray(schema);
  const blocks = isArraySchema ? schema : schema?.blocks || [];
  const payloadCfg = isArraySchema ? {} : schema?.payload || {};
  const map = payloadCfg.map || {};
  const staticVals = payloadCfg.static || {};
  const casts = payloadCfg.casts || {};
  const allow = Array.isArray(payloadCfg.allow) ? payloadCfg.allow : null;
  const includeRaw = Boolean(payloadCfg.includeRawFormData);

  const fields = flattenFields(blocks);
  const out = {};

  fields.forEach((field) => {
    if (!field?.name) return;
    const value = formData?.[field.name];
    // Choose destination key
    let key = field.payloadKey || map[field.name] || field.name;

    switch (field.type) {
      case 'date': {
        out[key] = normalizeDate(value);
        break;
      }
      case 'dateRange': {
        const startKey = field.payloadRangeKeys?.start || `${key}Start`;
        const endKey = field.payloadRangeKeys?.end || `${key}End`;
        out[startKey] = normalizeDate(value?.start);
        out[endKey] = normalizeDate(value?.end);
        break;
      }
      case 'nested': {
        if (isObject(value)) {
          // Shallow merge nested object into payload using optional prefix
          const prefix = field.payloadPrefix || '';
          Object.entries(value).forEach(([k, v]) => {
            const dest = prefix ? `${prefix}${k}` : k;
            out[dest] = v;
          });
        }
        break;
      }
      case 'radio': {
        // Handle Yes/No radio buttons -> boolean conversion
        if (value === 'Yes' || value === 'No') {
          out[key] = value === 'Yes';
        } else {
          out[key] = value ?? '';
        }
        break;
      }
      case 'repeatableSection':
      case 'expenseRows': {
        // Copy array data as-is for repeatable sections and expense rows
        // Transform function will handle further processing
        out[key] = value ?? [];
        break;
      }
      default: {
        out[key] = value ?? '';
      }
    }

    // Apply field-level transform if specified
    if (field.payloadTransform && typeof field.payloadTransform === 'function') {
      try {
        out[key] = field.payloadTransform(out[key]);
      } catch (e) {
        console.warn('Error applying payloadTransform for field', field.name, e);
      }
    }

    // Apply casting if defined for destination key
    const castType = casts[key];
    if (castType && out[key] !== '') {
      switch (castType) {
        case 'int':
        case 'integer':
          out[key] = Number.isNaN(parseInt(out[key])) ? out[key] : parseInt(out[key]);
          break;
        case 'float':
        case 'number':
          out[key] = Number.isNaN(parseFloat(out[key])) ? out[key] : parseFloat(out[key]);
          break;
        case 'string':
          out[key] = out[key] == null ? '' : String(out[key]);
          break;
        default:
          break;
      }
    }
  });

  // Merge static defaults without overwriting present values
  Object.entries(staticVals).forEach(([k, v]) => {
    if (out[k] === undefined || out[k] === '') out[k] = v;
  });

  if (includeRaw) {
    out.rawFormData = JSON.stringify(formData ?? {});
  }

  // Apply schema-level transform if provided
  let finalPayload = out;
  if (typeof payloadCfg.transform === 'function') {
    try {
      finalPayload = payloadCfg.transform(out, formData) || out;
    } catch (e) {
      console.error('[payload] Error applying schema transform:', e);
    }
  }

  // Apply allow-list filtering if provided
  if (allow) {
    const pruned = {};
    allow.forEach((k) => {
      let val;
      if (Object.prototype.hasOwnProperty.call(finalPayload, k)) {
        val = finalPayload[k];
      } else if (formData && Object.prototype.hasOwnProperty.call(formData, k)) {
        val = formData[k];
      } else {
        // Provide sensible typed defaults when missing, based on casts
        const castType = casts[k];
        if (castType === 'int' || castType === 'integer' || castType === 'float' || castType === 'number') {
          val = 0;
        } else {
          val = '';
        }
      }

      const castType = casts[k];
      // Always normalize/cast when a cast is declared
      if (castType) {
        switch (castType) {
          case 'int':
          case 'integer':
            val = Number.isNaN(parseInt(val)) ? 0 : parseInt(val);
            break;
          case 'float':
          case 'number':
            val = Number.isNaN(parseFloat(val)) ? 0 : parseFloat(val);
            break;
          case 'string':
            val = val == null ? '' : String(val);
            break;
          default:
            break;
        }
      }
      pruned[k] = val;
    });
    return pruned;
  }

  return finalPayload;
}

export default buildPayloadFromSchema;
